import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ds';
import { Meter, StereoField, MusicalContext, Status } from './TechnicalMeters';
import { readouts, peakStatus, monoStatus, formatDuration } from '../../utils/measurements';

const Section = ({ title, children }) => (
  <section className="flex flex-col gap-space-4">
    <h3 className="m-0 font-display text-project text-ink">{title}</h3>
    {children}
  </section>
);

const Readout = ({ label, value, unit, children }) => (
  <div className="flex flex-col gap-space-1 p-space-4 rounded-radius-md bg-base">
    <span className="text-caption text-muted">{label}</span>
    <span className="font-mono text-data-lg text-ink">
      {value === null ? '–' : value}{value !== null && unit ? <span className="text-data text-muted"> {unit}</span> : null}
    </span>
    {children}
  </div>
);

const dbText = (db) => (db === null ? null : db.toFixed(1).replace('-', '−'));

/**
 * Full metering for one version: the raw measurements behind the plain-language analysis. Beside the
 * chat on wide screens; over it on narrow ones.
 */
export const MeteringDrawer = ({ version, onClose }) => {
  const closeRef = useRef(null);
  useEffect(() => {
    closeRef.current?.focus();
  }, [version?.id]);

  if (!version) return null;
  const m = readouts(version.analysis);

  return (
    <aside
      aria-label="Full metering"
      onKeyDown={(event) => event.key === 'Escape' && onClose()}
      className="fixed inset-0 z-[90] lg:static lg:z-auto lg:w-[420px] flex-none flex flex-col bg-chrome font-text text-ink"
    >
      <div className="flex items-start justify-between gap-space-3 px-space-6 py-space-4">
        <div className="flex flex-col gap-space-1 min-w-0">
          <span className="text-label text-muted">Full metering</span>
          <span className="font-mono text-data-sm text-ink truncate">V{version.version} · {version.filename}</span>
        </div>
        <Button ref={closeRef} variant="icon" icon={X} aria-label="Close full metering" onClick={onClose} />
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain px-space-6 pb-space-8 flex flex-col gap-space-8">
        <Section title="Level">
          <div className="grid grid-cols-2 gap-space-3">
            <Readout label="Peak" value={dbText(m.peakDb)} unit="dBFS"><Status status={peakStatus(m.peakDb)} /></Readout>
            <Readout label="Average level (RMS)" value={dbText(m.rmsDb)} unit="dBFS" />
            <Readout label="Punch (crest factor)" value={dbText(m.crestDb)} unit="dB" />
            <Readout label="Length" value={formatDuration(m.duration)} />
          </div>
        </Section>
        <Section title="Tone">
          <Meter label="Brightness" hint="Where the sound's energy centers" value={m.brightnessHz} min={0} max={8000} unit="Hz" digits={0} />
          {m.bands.map((band) => (
            <Meter key={band.label} label={band.label} hint="Compared with the other bands" value={band.db} min={-24} max={24} centered />
          ))}
        </Section>
        <Section title="Stereo">
          <StereoField width={m.width} correlation={m.correlation} monoStatus={monoStatus(m.correlation)} />
        </Section>
        <Section title="Musical context">
          <MusicalContext bpm={m.bpm} estimatedKey={m.key} />
        </Section>
      </div>
    </aside>
  );
};
